import { Component, DestroyRef, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import * as MenuActions from '@states/menu/menu.actions';
import { MenuKey } from '@enums/menu.enum';
import { LocationEditSelectors } from '@states/location-edit/location-edit.selector-types';
import { DeviceModel } from '@models/device.model';
import { Observable, take } from 'rxjs';
import { LocationEditActions } from '@states/location-edit/location-edit.action-types';
import * as _ from 'lodash';
import { ofType } from '@ngrx/effects';
import * as SharedActions from '@states/shared/shared.actions';
import { PreloaderColor } from '@enums/shared.enum';
import { LocationEditEffects } from '@effects/location-edit.effects';
import { ActivatedRoute, Router } from '@angular/router';
import { routerSegments } from '@consts/routes';
import { LocationModel } from '../../../../locations/location.model';
import { speakerBrandsStr } from '@consts/speaker.const';
import { BreadcrumbsService } from 'src/app/services/breadcrumbs.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { NgForOf, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { UiKitModule } from '../../../../shared/ui-kit/ui-kit.module';
import { UiButtonComponent } from '@ui';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-location-speakers-add',
  templateUrl: './location-speakers-add.component.html',
  styleUrls: ['./location-speakers-add.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatLabel,
    TranslatePipe,
    MatFormField,
    MatInput,
    MatSelect,
    MatOption,
    NgForOf,
    NgSwitch,
    NgSwitchCase,
    NgSwitchDefault,
    MatIcon,
    NgIf,
    UiKitModule,
    UiButtonComponent,
  ],
})
export class LocationSpeakersAddComponent implements OnInit {
  public SpeakerBrands = DeviceModel.SpeakerBrands;
  public speakerBrandsStr = speakerBrandsStr;
  public PreloaderColor = PreloaderColor;

  public selectDeviceState$: Observable<DeviceModel.DeviceState> = this.store$.pipe(
    select(LocationEditSelectors.selectDeviceState),
  );
  public selectSelectedLocation$: Observable<LocationModel.LocationItem> = this.store$.pipe(
    select(LocationEditSelectors.selectedLocation),
  );

  public speakerFormGroup: UntypedFormGroup;
  public models = Object.values(DeviceModel.SpeakerBrands);
  public hidePassword = true;

  public deviceState: DeviceModel.DeviceState = {};

  public isEdit = false;

  constructor(
    private store$: Store,
    private fb: UntypedFormBuilder,
    private locationEditEffects: LocationEditEffects,
    private route: ActivatedRoute,
    private router: Router,
    private breadcrumbsService: BreadcrumbsService,
    private translateService: TranslateService,
    private destroyRef: DestroyRef,
  ) {}

  public get selectedModel() {
    return this.speakerFormGroup.get('model').value;
  }

  ngOnInit(): void {
    this.store$.dispatch(LocationEditActions.resetDeviceState());
    this.store$.dispatch(
      MenuActions.setActiveLevel2Menu({
        activeLevel2: MenuKey.addSpeaker,
      }),
    );

    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      if (params['speakerId']) {
        this.store$.dispatch(LocationEditActions.setSelectedSpeakerById({ speakerId: params['speakerId'] }));
        this.isEdit = true;
      }
    });

    this.selectDeviceState$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((deviceState) => {
      this.deviceState = deviceState;
    });

    this.speakerFormGroup = this.fb.group({
      _id: [''],
      name: ['Untitled speaker', Validators.required],
      type: [DeviceModel.DeviceType.Speaker, Validators.required],
      model: ['', Validators.required],
      ipAddress: ['', Validators.required],
      port: [''],
      username: ['', Validators.required],
      password: ['', Validators.required],
      sipUrl: [''],
      displayName: [''],
      cameras: [{}],
    });

    this.store$
      .pipe(select(LocationEditSelectors.selectedSpeaker), takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe((speaker) => {
        const breadcrumbs: { name: string; route?: string }[] = [
          {
            name: 'Speakers',
            route: routerSegments.speakers,
          },
        ];
        if (speaker) {
          this.speakerFormGroup.patchValue({
            _id: speaker._id,
            name: speaker.name,
            type: DeviceModel.DeviceType.Speaker,
            model: speaker.model,
            ipAddress: speaker.ipAddress,
            port: speaker.port,
            username: speaker.username,
            password: speaker.password,
            sipUrl: speaker.sipUrl,
            displayName: speaker.displayName,
            cameras: speaker.cameras,
          });
          breadcrumbs.push({
            name: speaker.name,
          });
        } else {
          breadcrumbs.push({
            name: 'Add speaker',
          });
        }
        this.breadcrumbsService.updateBreadcrumb(this.route, this.router, breadcrumbs);
      });
    this.speakerFormGroup.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((val) => {
      if (this.selectedModel === DeviceModel.SpeakerBrands.SIP) {
        this.store$.dispatch(LocationEditActions.testSpeakerSuccess());
      } else {
        if (!this.deviceState.testSuccess) {
          this.store$.dispatch(
            LocationEditActions.setDeviceState({ deviceStateChange: { testOngoing: false } }),
          );
        }
      }
      this.store$.dispatch(LocationEditActions.setSelectedSpeaker({ selectedSpeaker: _.cloneDeep(val) }));
    });

    this.locationEditEffects.pressCreate$
      .pipe(takeUntilDestroyed(this.destroyRef), ofType(LocationEditActions.pressCreate))
      .subscribe((_) => {
        this.store$.dispatch(SharedActions.setIsSaving({ isSaving: true }));
        this.store$.dispatch(LocationEditActions.addSpeaker());
      });

    this.locationEditEffects.addSpeakerSuccess$
      .pipe(takeUntilDestroyed(this.destroyRef), ofType(LocationEditActions.addSpeakerSuccess))
      .subscribe((_) => {
        this.store$.dispatch(SharedActions.setIsSaving({ isSaving: false }));
        this.store$.dispatch(
          SharedActions.showMessage({
            success: this.translateService.instant('TOASTS.ENTITY_CREATED_SUCCESS', { entity: 'Speaker' }),
          }),
        );
        this.router.navigate([this.isEdit ? '../../' : '../', routerSegments.speakers], {
          relativeTo: this.route,
        });
      });

    this.locationEditEffects.testSpeakerSuccess$
      .pipe(takeUntilDestroyed(this.destroyRef), ofType(LocationEditActions.testSpeakerSuccess))
      .subscribe((_) => {
        this.speakerFormGroup.disable();
      });
  }

  public test() {
    if (!this.speakerFormGroup.get('model').value) {
      return;
    }
    this.store$.dispatch(LocationEditActions.testSpeaker());
  }

  public testSip() {
    this.store$.dispatch(LocationEditActions.testSpeaker());
  }
}
